import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Mail, Play, Send, Eye, History, Clock, Rocket } from "lucide-react";

import { api, apiErrorMessage } from "@/lib/api";
import { AutomationStoppedPill, useAutomationsStatus } from "@/components/AutomationStoppedPill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PERIOD_META = {
  weekly:    { title: "Semanal",    desc: "Semana anterior (lun-dom). Comparativa: semana previa.",   eyebrow: "📊" },
  monthly:   { title: "Mensual",    desc: "Mes anterior completo. Comparativa: mes previo.",           eyebrow: "📈" },
  quarterly: { title: "Trimestral", desc: "Q1/Q2/Q3/Q4 (1-ene, 1-abr, 1-jul, 1-oct). Comparativa: trim. previo.", eyebrow: "📊" },
  yearly:    { title: "Anual",      desc: "Año anterior completo. Comparativa: año previo.",            eyebrow: "🏆" },
};

const DOW_LABELS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const MONTHS = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function fmtNextRun(iso) {
  if (!iso) return "—";
  // ISO: "2026-09-21 08:00:00+02:00"
  const s = iso.replace(" ", "T");
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("es-ES", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid",
  });
}

export default function InformesAutoPage() {
  const qc = useQueryClient();
  const autoQ = useAutomationsStatus();
  const automationsStopped = autoQ.data && !autoQ.data.enabled;
  const [drafts, setDrafts] = useState({});

  const cfgQ = useQuery({
    queryKey: ["reports-config"],
    queryFn: async () => (await api.get("/reports/config")).data,
  });
  const hisQ = useQuery({
    queryKey: ["reports-history"],
    queryFn: async () => (await api.get("/reports/history?limit=20")).data,
  });

  const cfg = cfgQ.data || {};

  const saveMut = useMutation({
    mutationFn: (body) => api.put("/reports/config", body),
    onSuccess: (r) => {
      const nrt = r?.data?.next_run_time;
      toast.success(nrt ? `Guardado · próximo envío ${fmtNextRun(nrt)}` : "Configuración guardada");
      qc.invalidateQueries({ queryKey: ["reports-config"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const sendTestMut = useMutation({
    mutationFn: (type) => api.post(`/reports/${type}/send-test`),
    onSuccess: (r) => {
      if (r?.data?.ok) toast.success("Test enviado a tu email · revisa la bandeja");
      else toast.error(`No enviado: ${r?.data?.error || "config incompleta"}`);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const sendNowMut = useMutation({
    mutationFn: (type) => api.post(`/reports/${type}/send-now`),
    onSuccess: (r) => {
      if (r?.data?.ok) toast.success(`Reporte enviado a ${r?.data?.recipients?.length || 0} destinatarios`);
      else toast.error(`No enviado: ${r?.data?.error || "config incompleta"}`);
      qc.invalidateQueries({ queryKey: ["reports-history"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const sendAllMut = useMutation({
    mutationFn: () => api.post(`/reports/send-all-now?only_to_me=true`),
    onSuccess: (r) => {
      const ok = r?.data?.sent || 0;
      toast.success(`${ok}/4 informes enviados a tu email`);
      qc.invalidateQueries({ queryKey: ["reports-history"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const setDraft = (type, patch) => setDrafts((d) => ({ ...d, [type]: { ...(d[type] || {}), ...patch } }));

  const getEffective = (type) => {
    const draft = drafts[type] || {};
    const base = cfg[type] || { recipients: [], enabled: true, schedule: {} };
    return {
      recipients: draft.recipients ?? base.recipients ?? [],
      recipientsRaw: draft.recipientsRaw ?? (base.recipients || []).join(", "),
      enabled: draft.enabled ?? base.enabled ?? true,
      schedule: { ...(base.schedule || {}), ...(draft.schedule || {}) },
      next_run_time: base.next_run_time,
    };
  };

  const handleSave = (type) => {
    const eff = getEffective(type);
    const recipients = eff.recipientsRaw.split(/[,\s;]+/).map((s) => s.trim()).filter(Boolean);
    saveMut.mutate({ type, recipients, enabled: eff.enabled, schedule: eff.schedule });
  };

  const confirmAndSendNow = (type) => {
    const eff = getEffective(type);
    const recips = (eff.recipientsRaw || "").split(/[,\s;]+/).map((s) => s.trim()).filter(Boolean);
    const target = recips.length ? recips.join(", ") : "todos los admins activos";
    const label = PERIOD_META[type]?.title || type;
    const ok = window.confirm(
      `¿Enviar AHORA el informe "${label}"?\n\n` +
      `Se enviará por email (Resend) a: ${target}\n\n` +
      `Guarda antes los destinatarios si los has editado.`,
    );
    if (ok) sendNowMut.mutate(type);
  };

  const confirmAndSendAll = () => {
    const ok = window.confirm(
      "¿Enviar los 4 informes AHORA?\n\n" +
      "Semanal + Mensual + Trimestral + Anual llegarán a tu email personal " +
      "(no se envían a los destinatarios configurados; es un envío de baseline seguro).\n\n" +
      "Perfecto para verificar formato y datos justo tras un deploy.",
    );
    if (ok) sendAllMut.mutate();
  };

  const renderScheduleFields = (type, sched) => {
    const hour = Number.isInteger(sched.hour) ? sched.hour : 8;
    const minute = Number.isInteger(sched.minute) ? sched.minute : 0;
    const dow = Number.isInteger(sched.day_of_week) ? sched.day_of_week : 0;
    const dom = Number.isInteger(sched.day_of_month) ? sched.day_of_month : 1;
    const month = Number.isInteger(sched.month) ? sched.month : 1;

    const patch = (extra) => setDraft(type, { schedule: { ...sched, ...extra } });

    const HourMinute = (
      <div className="flex items-center gap-2">
        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
        <Select value={String(hour)} onValueChange={(v) => patch({ hour: parseInt(v, 10) })}>
          <SelectTrigger className="h-8 w-[70px]" data-testid={`sched-hour-${type}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 24 }, (_, i) => (
              <SelectItem key={i} value={String(i)}>{String(i).padStart(2, "0")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-muted-foreground">:</span>
        <Select value={String(minute)} onValueChange={(v) => patch({ minute: parseInt(v, 10) })}>
          <SelectTrigger className="h-8 w-[70px]" data-testid={`sched-minute-${type}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[0, 15, 30, 45].map((m) => (
              <SelectItem key={m} value={String(m)}>{String(m).padStart(2, "0")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-1">Madrid</span>
      </div>
    );

    if (type === "weekly") {
      return (
        <div className="space-y-2">
          <Label className="text-xs">Día y hora</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={String(dow)} onValueChange={(v) => patch({ day_of_week: parseInt(v, 10) })}>
              <SelectTrigger className="h-8 w-[130px]" data-testid={`sched-dow-${type}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOW_LABELS.map((n, i) => (
                  <SelectItem key={i} value={String(i)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {HourMinute}
          </div>
        </div>
      );
    }
    if (type === "monthly") {
      return (
        <div className="space-y-2">
          <Label className="text-xs">Día del mes y hora</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={String(dom)} onValueChange={(v) => patch({ day_of_month: parseInt(v, 10) })}>
              <SelectTrigger className="h-8 w-[80px]" data-testid={`sched-dom-${type}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <SelectItem key={d} value={String(d)}>día {d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {HourMinute}
          </div>
        </div>
      );
    }
    if (type === "quarterly") {
      return (
        <div className="space-y-2">
          <Label className="text-xs">Día 1 de cada trimestre (ene · abr · jul · oct) y hora</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={String(dom)} onValueChange={(v) => patch({ day_of_month: parseInt(v, 10) })}>
              <SelectTrigger className="h-8 w-[80px]" data-testid={`sched-dom-${type}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <SelectItem key={d} value={String(d)}>día {d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {HourMinute}
          </div>
        </div>
      );
    }
    // yearly
    return (
      <div className="space-y-2">
        <Label className="text-xs">Mes, día y hora</Label>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={String(month)} onValueChange={(v) => patch({ month: parseInt(v, 10) })}>
            <SelectTrigger className="h-8 w-[130px]" data-testid={`sched-month-${type}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((n, i) => (
                <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(dom)} onValueChange={(v) => patch({ day_of_month: parseInt(v, 10) })}>
            <SelectTrigger className="h-8 w-[80px]" data-testid={`sched-dom-${type}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <SelectItem key={d} value={String(d)}>día {d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {HourMinute}
        </div>
      </div>
    );
  };

  const openPreview = async (type) => {
    const token = localStorage.getItem("sofi_token");
    try {
      const r = await fetch(`${api.defaults.baseURL}/reports/${type}/preview`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const html = await r.text();
      const blob = new Blob([html], { type: "text/html" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) {
      toast.error("No se pudo abrir el preview");
    }
  };

  return (
    <div className="space-y-6" data-testid="informes-auto-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2">
            <Mail className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Informes automáticos</h1>
            <p className="text-sm text-muted-foreground">
              Reportes por email con margen de venta, rentabilidad, pipeline y ranking por agente.
              Zona horaria: <span className="font-medium">Europe/Madrid</span>.
            </p>
          </div>
        </div>
        <Button
          size="sm" variant="default"
          className="bg-amber-600 hover:bg-amber-700"
          onClick={confirmAndSendAll}
          disabled={sendAllMut.isPending}
          data-testid="send-all-now-btn"
        >
          {sendAllMut.isPending
            ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            : <Rocket className="mr-1.5 h-3.5 w-3.5" />}
          Enviarme los 4 informes ahora
        </Button>
      </div>

      {cfgQ.isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {["weekly", "monthly", "quarterly", "yearly"].map((type) => {
            const meta = PERIOD_META[type];
            const eff = getEffective(type);
            return (
              <Card key={type} data-testid={`report-card-${type}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-base">
                    <span>{meta.eyebrow} {meta.title}</span>
                    <Badge variant={eff.enabled ? "default" : "secondary"} className={eff.enabled ? "bg-emerald-100 text-emerald-800" : ""}>
                      {eff.enabled ? "Activo" : "Pausado"}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="text-xs">{meta.desc}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {renderScheduleFields(type, eff.schedule)}

                  {automationsStopped ? (
                    <div className="flex items-center" data-testid={`next-run-${type}`}>
                      <AutomationStoppedPill testid={`automation-stopped-${type}`} />
                    </div>
                  ) : (
                    <div className="rounded-md bg-primary/5 border border-primary/10 px-3 py-2 text-xs text-primary flex items-center gap-2" data-testid={`next-run-${type}`}>
                      <Clock className="h-3.5 w-3.5" />
                      <span>Próximo envío: <span className="font-medium">{fmtNextRun(eff.next_run_time)}</span></span>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label className="text-xs">Destinatarios (separados por coma o espacio)</Label>
                    <Input
                      value={eff.recipientsRaw}
                      onChange={(e) => setDraft(type, { recipientsRaw: e.target.value })}
                      placeholder="eduardo@viajadverdad.com, beatriz@viajadverdad.com"
                      data-testid={`report-recipients-${type}`}
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Si dejas vacío, se envía a todos los admins activos.
                    </p>
                  </div>

                  <label className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
                    <span className="text-sm font-medium">Envío automático activo</span>
                    <Switch
                      checked={eff.enabled}
                      onCheckedChange={(v) => setDraft(type, { enabled: v })}
                      data-testid={`report-enabled-${type}`}
                    />
                  </label>

                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button size="sm" variant="default" onClick={() => handleSave(type)} disabled={saveMut.isPending} data-testid={`report-save-${type}`}>
                      {saveMut.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
                      Guardar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openPreview(type)} data-testid={`report-preview-${type}`}>
                      <Eye className="mr-1.5 h-3.5 w-3.5" /> Ver preview
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => sendTestMut.mutate(type)} disabled={sendTestMut.isPending} data-testid={`report-test-${type}`}>
                      {sendTestMut.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
                      Enviar a mí
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => confirmAndSendNow(type)} disabled={sendNowMut.isPending} data-testid={`report-send-${type}`}>
                      {sendNowMut.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1.5 h-3.5 w-3.5" />}
                      Enviar ahora
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" /> Historial de envíos (últimos 20)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hisQ.isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
            </div>
          ) : (hisQ.data?.items || []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Sin envíos todavía.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Fecha</TableHead>
                    <TableHead className="text-xs">Tipo</TableHead>
                    <TableHead className="text-xs">Asunto</TableHead>
                    <TableHead className="text-xs">Destinatarios</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(hisQ.data?.items || []).map((h, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{h.sent_at?.slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell className="text-xs">
                        <Badge variant="outline">{h.type}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">{h.subject}</TableCell>
                      <TableCell className="text-xs">{(h.recipients || []).join(", ")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
